import { MathematicsMinMaxConfiguration } from "@arbeidsark/shared"
import { useMemo, useState } from "react"
import styled, { useTheme } from "styled-components"
import { Button } from "~/components"
import NumberInput from "~/components/Input/NumberInput"
import { Icon, Modal } from "~/ur-react-components"
import { useTranslate } from "~/ur-react-hooks"

const Layout = styled.div`
  background: white;
  border-radius: 0.5rem;

  width: 328px;

  padding: 1rem;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
`

const Title = styled.h4`
  margin: 0;
  font-weight: 500;
  color: ${(props) => props.theme.colors.gray0};
  text-transform: uppercase;
`

const HeaderCloseButton = styled.button`
  outline: none;
  border: none;

  cursor: pointer;
  background: ${(props) => props.theme.colors.gray80};
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${(props) => props.theme.colors.gray70};
  }
`

const MinMaxWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const SectionLabel = styled.label`
  display: inline-block;
  margin-top: 1.5rem;
  margin-bottom: 8px;
  color: ${(props) => props.theme.colors.gray0};
`

const SaveButtonWrapper = styled.div`
  margin-top: 1.5rem;
`

export interface MinMaxDetailConfigurationModalProps {
  isOpen: boolean
  onClose: (newConfiguration?: MathematicsMinMaxConfiguration) => void

  configuration?: unknown

  pageIndex: number
  moduleIndex: number
}

const MinMaxDetailConfigurationModal: React.FC<
  MinMaxDetailConfigurationModalProps
> = ({
  isOpen,
  onClose,
  pageIndex,
  moduleIndex,
  configuration: _configuration,
}) => {
  const translations = useTranslate({
    page: "worksheets.labels.page",
    task: "worksheets.labels.task",
    configure: "worksheets.labels.configure",
    setLowestHighest: "worksheets.arithmetic-detail-modal.set-lowest-highest",
    save: "common.save",
  })

  const parsedConfiguration = useMemo(
    () => MathematicsMinMaxConfiguration.safeParse(_configuration),
    [_configuration],
  )

  const [configuration, setConfiguration] =
    useState<MathematicsMinMaxConfiguration>(
      parsedConfiguration.success
        ? parsedConfiguration.data
        : {
            min: 1,
            max: 10,
          },
    )

  const theme = useTheme()

  return (
    <Modal open={isOpen} onClose={() => onClose()}>
      <Layout>
        <Header>
          <Title>
            {translations.page} {pageIndex + 1} / {translations.task}{" "}
            {moduleIndex + 1} / {translations.configure}
          </Title>
          <HeaderCloseButton onClick={() => onClose()}>
            <Icon icon="close" size="14px" type="solid" />
          </HeaderCloseButton>
        </Header>
        <SectionLabel>{translations.setLowestHighest}</SectionLabel>
        <MinMaxWrapper>
          <NumberInput
            textAlign="center"
            value={configuration?.min ?? 1}
            onChange={(value) => {
              setConfiguration({
                ...configuration,
                min: value,
              })
            }}
          />
          <Icon icon="caret-right" type="solid" color={theme.colors.gray50} />
          <NumberInput
            textAlign="center"
            value={configuration?.max ?? 10}
            onChange={(value) => {
              setConfiguration({
                ...configuration,
                max: value,
              })
            }}
          />
        </MinMaxWrapper>
        <SaveButtonWrapper>
          <Button
            width="100%"
            type="button"
            onClick={() => onClose(configuration)}
          >
            {translations.save}
          </Button>
        </SaveButtonWrapper>
      </Layout>
    </Modal>
  )
}

export default MinMaxDetailConfigurationModal
